import classNames from 'classnames';
import type { JSX } from 'react';
import React, { type CSSProperties } from 'react';
import type { GroupBase, OptionProps, SingleValueProps } from 'react-select-modern';

import type { ICreativeAttributes } from '@feathr/blackbox';
import CampaignChip from '@feathr/extender/components/CampaignChip';

import CreativeThumbnail from '../CreativeThumbnail';

import * as styles from './SelectOptions.css';

function CreativeOption<
  T extends ICreativeAttributes,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<T> = GroupBase<T>,
>(props: OptionProps<T, IsMulti, Group>): JSX.Element {
  const { data, innerProps, isFocused, isSelected } = props;

  return (
    <div
      {...innerProps}
      className={classNames('react-select__option', styles.optionGrid, {
        'react-select__option--is-focused': isFocused,
        'react-select__option--is-selected': isSelected,
      })}
    >
      <div className={styles.optionGridCell}>
        <CreativeThumbnail creative={data} height={100} width={100} />
      </div>
      <div className={styles.optionGridCell}>
        <span className={styles.truncate}>{data.name || '-'}</span>
      </div>
      <div className={styles.optionGridCell}>
        <span className={styles.truncate}>
          {data.width && data.height ? `${data.width}×${data.height}` : '-'}
        </span>
      </div>
      <div className={styles.optionGridCell}>
        {!!data.parent && <CampaignChip id={data.parent} linkToItem={false} />}
      </div>
    </div>
  );
}

function CreativeSingleValue<
  T extends ICreativeAttributes,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<T> = GroupBase<T>,
>(props: SingleValueProps<T, IsMulti, Group>): JSX.Element {
  const { data, innerProps, getStyles } = props;

  return (
    <div
      {...innerProps}
      className={classNames('react-select__single-value', styles.optionGrid)}
      style={getStyles('singleValue', props) as CSSProperties}
    >
      <div className={styles.optionGridCell}>
        <span>{data.name || '-'}</span>
      </div>
      <div className={styles.optionGridCell}>
        <span>{data.width && data.height ? `${data.width}×${data.height}` : '-'}</span>
      </div>
      <div className={styles.optionGridCell}>
        {!!data.parent && <CampaignChip id={data.parent} linkToItem={false} />}
      </div>
    </div>
  );
}

export { CreativeOption, CreativeSingleValue };
